// mapにSourceとLayerをまとめて追加
import { SourceSpecification } from "maplibre-gl";
import { anyType, dataLoadedResultType, fiwareDataItemType, nonLngLatDataType } from "../types";
import { defaultStyle, generateFiwarePolygonFilter, generateLayers } from "../lib/smartCityStyle";
import { DataType } from "../types/lib/types";
import { TileUrlInfo, UrlsDataType } from "../types/lib/useGetTileUrls";
import { ALERT_TILE_URL } from "./getFeatures";
import { MapStyleConfigType, MapStyleSourcesObjType } from "../types/components/mapStyleConfig";


const DISTANCE_MEASUREMENT = 'distance-measurement/result-line-bg';
const DISTANCE_BUILDING = 'gsi/建築物4';

export const mapLayerControl = (
    map: maplibregl.Map,
    data: {
        fiwareData: { [key: string]: anyType; },
        fiawreCatalogItems: fiwareDataItemType[],
        dataPngUrls: UrlsDataType[],
        rasterUrls: UrlsDataType[],
        vectorTileUrls: UrlsDataType[],
        tileUrls: TileUrlInfo[],
        menuItems: DataType[],
        baseMapSources: MapStyleConfigType[],
    }
): Promise<dataLoadedResultType> => {
    return new Promise((resolve, reject) => {
        const { fiwareData, fiawreCatalogItems, dataPngUrls, rasterUrls, tileUrls, vectorTileUrls, menuItems } = data;

        const currentStyle = map.getStyle();
        const nonLngLatDataArr: nonLngLatDataType[] = [];

        try {
            /* **** 
             * 新しいソースを追加 
             * ****/
            const newSources: { [key: string]: SourceSpecification } = {};

            /* メインのデータソースを追加 */
            tileUrls.forEach((item) => {
                newSources[item.id] = {
                    type: "vector",
                    url: item.tileUrl
                };
            });

            vectorTileUrls.forEach((item) => {
                newSources[item.tileId] = {
                    type: "vector",
                    url: item.tileUrl,
                };
            });

            /* ラスタータイルのURLを設定ファイルから取得してソースを追加 */
            rasterUrls.forEach((item) => {
                newSources[item.tileId] = {
                    type: 'raster',
                    tiles: [item.tileUrl],
                    tileSize: 256,
                    minzoom: 10,
                    maxzoom: 18,
                };
            });

            /* データPNGのURLを設定ファイルから取得してソースを追加 */
            dataPngUrls.forEach((item) => {
                newSources[item.tileId] = {
                    type: 'raster',
                    tiles: [item.tileUrl],
                    tileSize: 256,
                    minzoom: 10,
                    maxzoom: 18
                };
            });

            /* fiwareソースを追加 */
            fiawreCatalogItems.forEach((fiwareItem) => {
                // tileIdがある場合は、idを追加
                if('tileId' in fiwareItem && fiwareItem.tileId) { 
                    if(fiwareItem.tileId in newSources) { return; }
                    newSources[fiwareItem.tileId] = {
                        type: 'vector',
                        url: `pmtiles://${ALERT_TILE_URL}`
                    };
                } else {
                    newSources[fiwareItem.dataId] = {
                        type: 'geojson',
                        data: fiwareData?.[fiwareItem.dataId] ?? { type: 'FeatureCollection', features: [] }
                    };
                }
            });

            /* 背景ソースを追加 */ 
            data.baseMapSources.forEach((item) => {
                item.sources.forEach((source) => {
                    newSources[(source as MapStyleSourcesObjType).id] = {
                        type: 'vector',
                        url: (source as MapStyleSourcesObjType).url
                    };
                });
            });

            /* **** 
             * 新しいレイヤーを追加 
             * ****/
            const pointSymbolLayers: maplibregl.LayerSpecification[] = [];
            const lineLayers: maplibregl.LayerSpecification[] = [];
            const polygonLayers: maplibregl.LayerSpecification[] = [];
            const rasterLayers: maplibregl.LayerSpecification[] = [];
            const labelLayers: maplibregl.LayerSpecification[] = [];

            menuItems.forEach((item) => {
                // パネルを追加
                if (item.metadata?.dataType === 'Alert') {
                    const data = fiwareData?.[(item as unknown as fiwareDataItemType).dataId];
                    if (!data) { return; }

                    const message = Array.isArray(data) ? data : [data];
                    nonLngLatDataArr.push({title: item.id, data: message, isShow: false});

                    return;
                }

                let id = item.tileId;
                if(item.dataType === 'fiware' && !('tileId' in item)) {
                    id = (item as fiwareDataItemType).dataId;
                }

                const sourceId = id;

                const layerArr = generateLayers(map, item, sourceId) as maplibregl.LayerSpecification[] ?? [];

                let colorFilter = undefined;
                let filter = undefined;

                // fiwareの場合、tileIdがある場合は、フィルターを追加
                if(item.dataType === 'fiware' && 'tileId' in item) {
                    const res = generateFiwarePolygonFilter(fiwareData, (item as unknown as fiwareDataItemType).dataId);
                    colorFilter = res?.fillColorExpression;
                    filter = res?.filter;
                }

                layerArr.forEach(layer => {
                    if(filter) {
                        (layer as anyType).filter = ['all', (layer as anyType).filter, filter];
                    }

                    if (layer.type === 'circle' || (layer.type === 'symbol' && layer.layout && layer.layout['icon-image'])) {
                        pointSymbolLayers.push(layer);
                    } else if (layer.type === 'symbol' && layer.layout && !layer.layout['icon-image']) {
                        labelLayers.push(layer);
                    } else if (layer.type === 'line') {
                        if(colorFilter && 'paint' in layer && layer.paint) { 
                            layer.paint['line-color'] = colorFilter; 
                        }
                        lineLayers.push(layer);
                    } else if (layer.type === 'fill') {
                        if(colorFilter && 'paint' in layer && layer.paint) { 
                            layer.paint['fill-color'] = colorFilter; 
                        }
                        polygonLayers.push(layer);
                    } else if (layer.type === 'raster') {
                        rasterLayers.push(layer);
                    }
                });
            });

            let baseMapLayers: maplibregl.LayerSpecification[] = [];

            // 背景地図のレイヤーを追加
            data.baseMapSources.forEach((item) => {
                const layerArr = defaultStyle(item.sources[0] as MapStyleSourcesObjType, item.id);
                baseMapLayers = layerArr;
            });


            const newStyle = {
                ...currentStyle,
                sources: {
                    ...currentStyle.sources,
                    ...newSources
                },
                layers: [
                    ...currentStyle.layers.filter(layer => layer.type === 'background'),
                    ...baseMapLayers,
                    ...currentStyle.layers.filter(layer => layer.type !== 'background')
                ]
            };

            // スタイルの読み込みが完了したら、styledataイベントを発火
            map.on('styledata', function onStyleData() {
                resolve({result: 'success', nonLngLatDataArr: nonLngLatDataArr.length > 0 ? nonLngLatDataArr : undefined});
                map.off('styledata', onStyleData);
            });

            map.setStyle(newStyle);

            rasterLayers.forEach((layer) => {
                map.addLayer(layer, DISTANCE_BUILDING);
            });
            polygonLayers.forEach((layer) => {
                map.addLayer(layer, DISTANCE_MEASUREMENT);
            });
            lineLayers.forEach((layer) => {
                map.addLayer(layer, DISTANCE_MEASUREMENT);
            });
            labelLayers.forEach((layer) => {
                map.addLayer(layer, DISTANCE_MEASUREMENT);
            });
            pointSymbolLayers.forEach((layer) => {
                map.addLayer(layer, DISTANCE_MEASUREMENT);
            });

        } catch (error) {
            console.error('Error in mapLayerControl:', error);
            // 必要に応じて、エラーハンドリングの追加処理を行う
            reject({result: 'error', nonLngLatDataArr: undefined});
        }
    });
};
